define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/when",
	"underscore",
	"csi",
	"csi/core/ExtendedPath",
	"csi/core/SpecBuilder",
	"csi/def",
	"csi/locale",
	"csi/middleware/customView/customViewService",
	"csi/reflection",
	"csi/store/_ClientRequestMixin",
	"csi/store/Nodes",
	"csi/xml",
	"./main",
	"./ExportClassStore"
], function(declare, lang, when, _, csi, ExtendedPath, SpecBuilder, def, locale, {getCustomViewAvailablePaths}, reflection, _ClientRequestMixin, Nodes, xml, Export, ExportClassStore) {

	const RE = /^(_CS_|BusinessObject|Configurable|Crew|Export|Expressions|ExternalExporterRegistry|Jasper|Locale|Module|Query|UserPreferences|ViewConfig|Workflow)/;
	const _mapFrom = {};
	const _mapTo = {};

	function _calculateCoverage(/*Node*/node) {
		// summary: calculate classes that can be reached
		const type = node.$Name;

		if (_mapTo[type]) {
			return;
		}

		const setTo = new Set();
		const defView = def.getClassViewDef(reflection.getClass(type), Export.VIEW);
		if (defView) {
			const specBuilder = new SpecBuilder(type, null, null, null, null, defView.m_headings);
			const additionalPreferencePaths = defView.getPageRelationship()?.m_prefPaths;
			const sourcePath = new ExtendedPath(type);
			const otherPaths = getCustomViewAvailablePaths(sourcePath, additionalPreferencePaths, specBuilder.configuredPaths);

			for (const path of otherPaths) {
				setTo.add(path.endType);
			}
		}

		const result = _mapTo[type] = [...setTo];

		// populate "from" map
		for (const destType of result) {
			csi.objectArrayAddValue(_mapFrom, destType, type);
		}
	}

	function _getString(/*Node*/node,/*Object*/typeMap) {
		// summary: join reachable classes together with newline
		var a = typeMap[node.$Name] || [];
		return a.sort().join("\n");
	}

	function _fillResults(/*Node[]*/data, /*Deferred*/dfd) {
		csi.forEachWithTimeout(
			data,
			10,
			function(group) {
				_.each(group, _calculateCoverage);
			},
			null,
			function() {
				// populate other attributes on nodes - have to wait until end so that _mapFrom is fully populated
				_.each(data, function(node) {
					xml.nodeCreateClientAttr(node, "ExportClassLocalized", xml.ATTR_STRING, locale.type(node.$Name));
					xml.nodeCreateClientAttr(node, "ExportClassReachableTo", xml.ATTR_STRING, _getString(node, _mapTo));
					xml.nodeCreateClientAttr(node, "ExportClassReachableFrom", xml.ATTR_STRING, _getString(node, _mapFrom));
				});

				// store is ready, so return
				dfd.resolve(data);
			}
		);
	}

	function _testClass(/*Class*/rc) {
		// summary: determine if class should be exported
		var type = rc.getType();

		// omit private classes and other setup classes
		if (rc.isPrivate() || type.match(RE)) {
			return false;
		}

		// allow classes that can be navigated to
		if (def.canNavigateToType(type)) {
			return true;
		}

		// allow classes with parent class that passes rules
		var parentClasses = rc.getPathClasses(reflection.PATH_PARENT);
		if (parentClasses) {
			return _.some(parentClasses, function(rcParent) {
				return (rc != rcParent) && _testClass(rcParent);
			});
		}

		// if no parent class, then accept classes that have been named
		return !!locale.parameterResource(type + ".Name");
	}

	const _InStore = declare([Nodes, _ClientRequestMixin], {
		bSimpleQueryEngine: true,

		_resolveData: function(/*Deferred*/dfd) {
			var data = csi.transform(ExportClassStore.query(), function(item) {
				var type = ExportClassStore.getIdentity(item);
				var rc = reflection.getClass(type);
				if (rc) {
					return xml.nodeCreateClientNode(rc.getURL(), reflection.TYPE_BUSINESS_OBJECT, type);
				}

				return null;
			}, this);

			_fillResults(data, dfd);
		}
	});

	const _inStore = new _InStore();

	const _OutStore = declare([Nodes, _ClientRequestMixin], {
		bSimpleQueryEngine: true,

		_resolveData: function(/*Deferred*/dfd) {
			when(_inStore.query(), lang.hitch(this, function(inNodes) {
				var inMap = csi.hashArray(inNodes, "$Name", Boolean);

				var data = csi.transform(reflection.getClasses(), function(rc) {
					var type = rc.getType();

					if (rc.abstract || inMap[type]) {
						return null;
					}

					if (!_testClass(rc)) {
						console.log("Export Coverage - ignore " + type);
						return null;
					}

					return xml.nodeCreateClientNode(rc.getURL(), reflection.TYPE_BUSINESS_OBJECT, type);
				});

				data = csi.sort(data || [], function(node) {
					return locale.type(node.$Name);
				});

				_fillResults(data, dfd);
			}));
		}
	});

	const _outStore = new _OutStore;

	return {
		In: _inStore,
		Out: _outStore
	};
});